import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, CheckCircle2, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";

export default function PublicAttest() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ok | expired | notfound | done
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Review & attest — TenantSeal";
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow"; document.head.appendChild(m);
    axios.get(`${API}/public/attest/${token}`)
      .then(({ data }) => { setData(data); setState("ok"); })
      .catch((e) => setState(e?.response?.status === 410 ? "expired" : "notfound"));
    return () => { document.head.removeChild(m); };
  }, [token]);

  const submit = async () => {
    if (!agreed) { toast.error("Please confirm the statement first."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/attest/${token}/complete`, {
        witness_name: name.trim() || null, relationship: relationship.trim() || null, statement_agreed: true,
      });
      setState("done");
    } catch (e) {
      if (e?.response?.status === 410) setState("expired");
      else toast.error(e?.response?.data?.detail || "Couldn't record your attestation. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (state === "loading")
    return <div className="min-h-screen grid place-items-center bg-[#FAF7F2]"><Loader2 className="animate-spin text-brand" /></div>;

  if (state === "notfound" || state === "expired")
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAF7F2] px-6 text-center">
        <div data-testid="attest-unavailable">
          <p className="font-heading text-2xl font-bold text-navy">Link unavailable</p>
          <p className="mt-2 text-[#64748B]">
            {state === "expired"
              ? "This witness link has already been used or is no longer active."
              : "This witness link is invalid. Please ask for a fresh link."}
          </p>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#FAF7F2] pb-[380px]">
      <header className="border-b border-[#E2E8F0] bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4"><Logo /></div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8" data-testid="attest-report">
        <h1 className="font-heading text-2xl font-bold text-navy">{data.report.property.address}</h1>
        <p className="text-sm text-[#64748B]">Move-in condition report · please review the photos below.</p>

        <div className="mt-6 space-y-8">
          {data.report.rooms.map((room, i) => (
            <section key={i} data-testid={`attest-room-${i}`}>
              <h2 className="font-heading text-lg font-semibold text-navy mb-3">{room.name}</h2>
              {room.photos.length === 0 ? (
                <p className="text-sm text-[#94A3B8] flex items-center gap-2"><Camera size={15} /> No photos in this room.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {room.photos.map((p) => (
                    <img key={p.photo_id} src={`${API}${p.image_path}`} alt={room.name}
                      className="w-full aspect-square object-cover rounded-xl border border-[#E2E8F0] bg-white" />
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      </main>

      {/* Fixed attestation sheet */}
      <div className="fixed bottom-0 inset-x-0 border-t border-[#E2E8F0] bg-white shadow-[0_-8px_30px_rgba(0,0,0,0.06)]">
        <div className="max-w-3xl mx-auto px-6 py-5">
          {state === "done" ? (
            <div className="text-center py-4" data-testid="attest-success">
              <CheckCircle2 size={36} className="mx-auto text-emerald-600" />
              <p className="mt-2 font-heading text-lg font-bold text-navy">Recorded — thank you.</p>
              <p className="text-sm text-[#64748B]">Your attestation has been saved with today's date.</p>
            </div>
          ) : (
            <div data-testid="attest-form">
              <div className="flex items-start gap-2 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] px-4 py-3">
                <ShieldCheck size={18} className="text-brand mt-0.5 shrink-0" />
                <p className="text-sm text-[#334155]">{data.statement}</p>
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-navy">Your name (optional)</Label>
                  <Input data-testid="attest-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Maria G." className="mt-1 rounded-xl" maxLength={100} />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-navy">Relationship (optional)</Label>
                  <Input data-testid="attest-relationship" value={relationship} onChange={(e) => setRelationship(e.target.value)} placeholder="e.g. roommate" className="mt-1 rounded-xl" maxLength={60} />
                </div>
              </div>
              <label className="mt-3 flex items-start gap-2.5 cursor-pointer">
                <Checkbox data-testid="attest-agree" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" />
                <span className="text-sm text-[#475569]">I agree with the statement above.</span>
              </label>
              <Button data-testid="attest-submit" onClick={submit} disabled={!agreed || submitting}
                className="mt-3 w-full rounded-xl bg-brand hover:bg-brand-hover text-white disabled:opacity-50">
                {submitting ? <><Loader2 size={16} className="mr-2 animate-spin" /> Recording…</> : "Confirm attestation"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
